import { FieldDefinition } from '../field-definition';
import { GenericValidator } from '../../generic-form/model/generic-validator';
import { GenericFormField } from '../../generic-form/model/generic-field';
import { ValidationTypes } from '../../generic-form/enums/validation-types';
import { ControlTypes } from '../../generic-form/enums/control-types';
import { InputTypes } from '../../generic-form/enums/input-type';
import { ValidationConverterService } from '../../services/validation-converter.service';
import { CustomHourRangeFilter } from '../../generic-table/filters/custom-hour-range-filter';
import { CustomTimeEditor } from '../../generic-table/editors/custom-time-editor';
import { BaseFieldBuilder } from './field-builder.base';
import { DEFAULT_TIME_DATE } from '../../constants/numeric-constants';

export class TimeFieldBuilder extends BaseFieldBuilder {
  private readonly validators: GenericValidator[] = [];
  private _required = false;
  private timeFilterDefaultDateValue = DEFAULT_TIME_DATE;

  constructor(key: string, label: string) {
    super(key, label, ControlTypes.Time);
    this._type = InputTypes.Time;
    this.timeFilterDefaultDateValue = DEFAULT_TIME_DATE;
  }

  required(): this {
    this._required = true;
    this.validators.push(new GenericValidator({ key: ValidationTypes.Required }));
    return this;
  }

  lessThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.LessThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  greaterThanField(fieldKey: string, fieldLocalization?: string): this {
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.GreaterThanField,
        value: fieldKey,
        fieldLocalization,
      }),
    );
    return this;
  }

  filterDefaultDate(value: string): this {
    this.timeFilterDefaultDateValue = value;
    return this;
  }

  build(): FieldDefinition {
    const column = this.getBaseColumnConfig();
    const localizedLabel = this.getLocalizationKey();

    this.configureFormatter(column);
    this.configureEditor(column);
    this.configureFilter(column);

    return {
      key: this.key,
      label: localizedLabel,
      column,
      fieldOptions: this.createFieldOptions(localizedLabel),
    };
  }

  private configureFormatter(column: any): void {
    if (column.formatter) {
      return;
    }

    column.formatter = (_row, _cell, value) => {
      if (!value || typeof value !== 'string') {
        return '';
      }

      if (value.includes('T')) {
        return value.split('T')[1]?.substring(0, 5) ?? '';
      }

      if (/^\d{2}:\d{2}:\d{2}/.test(value)) {
        return value.substring(0, 5);
      }

      if (/^\d{2}:\d{2}$/.test(value)) {
        return value;
      }

      return '';
    };
  }

  private configureEditor(column: any): void {
    if (this.readOnlyValue) {
      return;
    }

    column.createEditor = (localizationService: any) => {
      const validationConverter = new ValidationConverterService(localizationService);
      return {
        model: CustomTimeEditor,
        required: this._required,
        validator: validationConverter.toSlickGridValidator(
          this.validators,
          this._required,
          this._controlType,
        ),
      };
    };
  }

  private configureFilter(column: any): void {
    column.createFilter = (localizationService: any) => {
      const params: Record<string, any> = {
        localizationService,
        includeTime: false,
        defaultTimeDate: this.timeFilterDefaultDateValue ?? DEFAULT_TIME_DATE,
      };

      if (this.filterInputEmptyValueValue) {
        params.inputEmptyValue = this.filterInputEmptyValueValue;
      }

      return {
        model: CustomHourRangeFilter,
        params,
      };
    };
  }

  private createFieldOptions(localizedLabel: string): GenericFormField {
    return new GenericFormField({
      key: this.key,
      label: localizedLabel,
      controlType: this._controlType,
      type: this._type,
      required: this._required,
      validators: this.validators,
      hidden: this.hideInChildFormsValue,
      hiddenByDefault: this.hideByDefaultValue,
      readOnly: this.readOnlyValue,
      showReadOnlyInForm: this.showReadOnlyInFormValue,
      dependencies: this.dependencyRules.length ? this.dependencyRules : undefined,
      prefilter: this.prefilterOptions,
    });
  }
}
